import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDonation } from '../../contexts/DonationContext';
import images from '../../data/images.json';
import CountdownTimer from '../../components/CountdownTimer/CountdownTimer';
import ImpactModal from '../../components/ImpactModal/ImpactModal';
import './Home.css';

const Home: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedAmount, setSelectedAmount] = useState('25');
  const [donationType, setDonationType] = useState('monthly');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [previousSlide, setPreviousSlide] = useState<number | null>(null);
  const [slideDirection, setSlideDirection] = useState<'next' | 'prev'>('next');
  const [isAnimating, setIsAnimating] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [showImpactModal, setShowImpactModal] = useState(false);

  const heroSlides = [
    {
      image: images.hero.child,
      title1: t('home.hero.slide1.title1'),
      title2: t('home.hero.slide1.title2'),
      subtitle: t('home.hero.slide1.subtitle'),
      contentPosition: 'right', // Image on LEFT → Content on RIGHT
    },
    {
      image: images.hero.slider2,
      title1: t('home.hero.slide2.title1'),
      title2: t('home.hero.slide2.title2'),
      subtitle: t('home.hero.slide2.subtitle'),
      contentPosition: 'left', // Image on RIGHT → Content on LEFT
    },
  ];

  // Initialize after first render to prevent animation on load
  useEffect(() => {
    const timer = setTimeout(() => setIsInitialized(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const goToSlide = useCallback((index: number, direction: 'next' | 'prev' = 'next') => {
    if (isAnimating || index === currentSlide) return;
    setIsAnimating(true);
    setPreviousSlide(currentSlide);
    setSlideDirection(direction);
    setCurrentSlide(index);
    setTimeout(() => {
      setIsAnimating(false);
      setPreviousSlide(null);
    }, 900); // Match animation duration
  }, [isAnimating, currentSlide]);

  const nextSlide = useCallback(() => {
    if (isAnimating) return;
    const next = (currentSlide + 1) % heroSlides.length;
    goToSlide(next, 'next');
  }, [currentSlide, heroSlides.length, isAnimating, goToSlide]);

  const prevSlide = useCallback(() => {
    if (isAnimating) return;
    const prev = (currentSlide - 1 + heroSlides.length) % heroSlides.length;
    goToSlide(prev, 'prev');
  }, [currentSlide, heroSlides.length, isAnimating, goToSlide]);

  // Auto-play carousel
  useEffect(() => {
    if (!isInitialized) return;
    
    const interval = setInterval(() => {
      nextSlide();
    }, 5000);

    return () => clearInterval(interval);
  }, [nextSlide, isInitialized]);

  // Get slide class based on its state
  const getSlideClass = (index: number) => {
    const baseClass = 'hero-slide';
    
    // On initial load, just show first slide without animation
    if (!isInitialized) {
      return index === 0 ? `${baseClass} active` : baseClass;
    }
    
    if (index === currentSlide) {
      return `${baseClass} active slide-in-${slideDirection}`;
    }
    if (index === previousSlide) {
      return `${baseClass} slide-out-${slideDirection}`;
    }
    return baseClass;
  };

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section">
        {/* Navigation Arrows - Outside the wrapper for full-width positioning */}
        <button 
          className="carousel-arrow carousel-arrow-left" 
          onClick={prevSlide}
          aria-label="Previous slide"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15,18 9,12 15,6"></polyline>
          </svg>
        </button>
        <button 
          className="carousel-arrow carousel-arrow-right" 
          onClick={nextSlide}
          aria-label="Next slide"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="9,6 15,12 9,18"></polyline>
          </svg>
        </button>

        {/* Full-width Image Slides */}
        <div className="hero-slides-container">
          {heroSlides.map((slide, index) => (
            <div 
              key={index}
              className={`${getSlideClass(index)} content-${slide.contentPosition}`}
            >
              <img
                src={slide.image}
                alt={`Hero Slide ${index + 1}`}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = 'https://via.placeholder.com/1920x800/1a1a2e/f4c430?text=Slide';
                }}
              />
              {/* Dark overlay - changes based on content position */}
              <div className={`hero-slide-overlay overlay-${slide.contentPosition}`}></div>
            </div>
          ))}
        </div>

        {/* Content Overlay - Position changes based on current slide */}
        <div className={`hero-content content-${heroSlides[currentSlide].contentPosition}`}>
          <div className="hero-text">
            <span className="hero-tag">Charity & Donation</span>
            <h1 className="hero-title-1">{heroSlides[currentSlide].title1}</h1>
            <h1 className="hero-title-2">{heroSlides[currentSlide].title2}</h1>
            <div className="hero-underline"></div>
            <p className="hero-subtitle">{heroSlides[currentSlide].subtitle}</p>
            <button 
              className="learn-more-button"
              onClick={() => navigate('/donate-anonymously')}
            >
              {t('home.hero.learnMore')}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Carousel Dots */}
        <div className="carousel-dots">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              className={`carousel-dot ${index === currentSlide ? 'active' : ''}`}
              onClick={() => goToSlide(index, index > currentSlide ? 'next' : 'prev')}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </section>

      {/* Upcoming Events & Donation Section */}
      <section className="events-donation-section">
        <div className="container">
          <div className="events-donation-grid">
            <div className="upcoming-events">
              <h2 className="section-title">{t('home.upcomingEvents.title')}</h2>
              <div className="event-card">
                <img
                  src={images.events.education}
                  alt="Education Event"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = 'https://via.placeholder.com/400x250/f4c430/333333?text=Event';
                  }}
                />
                <div className="event-details">
                  <h3>{t('home.upcomingEvents.eventTitle')}</h3>
                  <p className="event-date">🕐 {t('home.upcomingEvents.date')}</p>
                  <p className="event-location">📍 {t('home.upcomingEvents.location')}</p>
                  <CountdownTimer targetDate={new Date('2026-01-01T16:30:00Z')} />
                </div>
              </div>
            </div>

            <div className="donation-widget">
              <div className="donation-amount-large">${selectedAmount}</div>
              <div className="donation-tabs">
                <button
                  className={`donation-tab ${donationType === 'monthly' ? 'active' : ''}`}
                  onClick={() => setDonationType('monthly')}
                >
                  {t('home.donation.monthly')}
                </button>
                <button
                  className={`donation-tab ${donationType === 'single' ? 'active' : ''}`}
                  onClick={() => setDonationType('single')}
                >
                  {t('home.donation.single')}
                </button>
              </div>
              <div className="donation-slider">
                <div className="slider-track">
                  {[0, 10, 25, 50, 75, 100].map((amount) => (
                    <div
                      key={amount}
                      className="slider-mark"
                      onClick={() => setSelectedAmount(amount.toString())}
                      style={{ cursor: 'pointer' }}
                    >
                      <span className="mark-label">${amount}</span>
                      {amount === parseInt(selectedAmount) && (
                        <div className="slider-indicator"></div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              <p className="donation-description">{t('home.donation.description')}</p>
              <button 
                className="donate-now-button"
                onClick={() => navigate('/donate-anonymously')}
              >
                {t('home.donation.button')}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Recent Causes Section */}
      <section className="recent-causes-section">
        <div className="container">
          <h2 className="section-title-large">{t('home.recentCauses.title')}</h2>
          <p className="section-subtitle">
            {t('home.recentCauses.subtitle').split('helps')[0]}
            <span className="highlight">{t('home.recentCauses.helps')}</span>
            {t('home.recentCauses.subtitle').split('helps')[1]}
          </p>
          <div className="causes-grid">
            <CauseCard
              image={images.causes.childEducation}
              progress={63}
              donation="53,458"
              goal="87,609"
              title={t('home.recentCauses.childEducation.title')}
              description={t('home.recentCauses.childEducation.description')}
              buttonText={t('home.recentCauses.childEducation.button')}
            />
            <CauseCard
              image={images.causes.drinkingWater}
              progress={42}
              donation="53,458"
              goal="87,609"
              title={t('home.recentCauses.drinkingWater.title')}
              description={t('home.recentCauses.drinkingWater.description')}
              buttonText={t('home.recentCauses.drinkingWater.button')}
            />
            <CauseCard
              image={images.causes.homelessPeople}
              progress={70}
              donation="53,458"
              goal="87,609"
              title={t('home.recentCauses.homelessPeople.title')}
              description={t('home.recentCauses.homelessPeople.description')}
              buttonText={t('home.recentCauses.homelessPeople.button')}
            />
          </div>
        </div>
      </section>

      {/* How Can You Help Section */}
      <section className="how-can-help-section">
        <div className="container">
          <div className="help-content">
            <div className="help-text">
              <h2 className="section-title-large">
                {t('home.howCanYouHelp.title')}
                <div className="title-underline"></div>
              </h2>
              <p>{t('home.howCanYouHelp.description')}</p>
              <button 
                className="learn-more-button-help"
                onClick={() => setShowImpactModal(true)}
              >
                {t('home.howCanYouHelp.learnMore')}
                <span className="arrow-icon">→</span>
              </button>
            </div>
            <div className="help-options">
              <div className="help-option">
                <img
                  src={images.howCanYouHelp.icons.media}
                  alt="Media"
                  className="help-icon-img"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
                <span>{t('home.howCanYouHelp.options.media')}</span>
              </div>
              <div className="help-option">
                <img
                  src={images.howCanYouHelp.icons.volunteer}
                  alt="Volunteer"
                  className="help-icon-img"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
                <span>{t('home.howCanYouHelp.options.becomeVolunteer')}</span>
              </div>
              <div className="help-option">
                <img
                  src={images.howCanYouHelp.icons.gift}
                  alt="Gift"
                  className="help-icon-img"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
                <span>{t('home.howCanYouHelp.options.makeGift')}</span>
              </div>
              <div className="help-option">
                <img
                  src={images.howCanYouHelp.icons.donation}
                  alt="Donation"
                  className="help-icon-img"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
                <span>{t('home.howCanYouHelp.options.sendDonation')}</span>
              </div>
              <div className="help-option">
                <img
                  src={images.howCanYouHelp.icons.helpingHand}
                  alt="Helping Hand"
                  className="help-icon-img"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
                <span>{t('home.howCanYouHelp.options.giveHelpingHand')}</span>
              </div>
            </div>
            <div className="help-image">
              <img
                src={images.howCanYouHelp.volunteer}
                alt="Volunteer"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = 'https://via.placeholder.com/400x500/fff9e6/333333?text=Volunteer';
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      <StatisticsSection
        statistics={[
          { icon: "❤️", number: "4512", label: t('home.statistics.causesSolved') },
          { icon: "👥", number: "1024", label: t('home.statistics.volunteers') },
          { icon: "👍", number: "5234", label: t('home.statistics.donators') },
          { icon: "😊", number: "77614", label: t('home.statistics.savedChildren') },
        ]}
      />

      {/* Latest Donaters Section */}
      <section className="latest-donaters-section">
        <div className="container">
          <h2 className="section-title-large">{t('home.latestDonaters.title')}</h2>
          <p className="section-subtitle">{t('home.latestDonaters.subtitle')}</p>
          <div className="donaters-grid">
            <DonaterCard
              image={images.donaters.chrisBinva}
              name="Rajesh Kumar"
              role="Business Owner, Patna"
              amount="₹25,000"
            />
            <DonaterCard
              image={images.donaters.jennyRose}
              name="Priya Sharma"
              role="Teacher, Gaya"
              amount="₹5,000"
            />
            <DonaterCard
              image={images.donaters.bennyDoe}
              name="Amit Singh"
              role="Social Worker, Bodh Gaya"
              amount="₹10,000"
            />
            <div className="become-donater-card">
              <h3>{t('home.latestDonaters.becomeDonator.title')}</h3>
              <p>{t('home.latestDonaters.becomeDonator.description')}</p>
              <button 
                className="join-now-button"
                onClick={() => navigate('/donate-anonymously')}
              >
                {t('home.latestDonaters.becomeDonator.button')}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Section */}
      <section className="blog-section">
        <div className="container">
          <h2 className="section-title-large">{t('home.blog.title')}</h2>
          <p className="section-subtitle">{t('home.blog.subtitle')}</p>
          <div className="blog-grid">
            <BlogCard image={images.blog.post1} />
            <BlogCard image={images.blog.post2} />
            <BlogCard image={images.blog.post3} />
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section">
        <div className="container">
          <div className="testimonial-content">
            <div className="testimonial-text">
              <h2 className="section-title-large">{t('home.testimonials.title')}</h2>
              <div className="quote-mark">"</div>
              <p className="testimonial-quote">{t('home.testimonials.quote')}</p>
              <div className="testimonial-author">
                <strong>{t('home.testimonials.author')}</strong>
                <span>{t('home.testimonials.position')}</span>
              </div>
              <div className="testimonial-dots">
                <span className="dot active"></span>
                <span className="dot active"></span>
                <span className="dot"></span>
              </div>
            </div>
            <div className="testimonial-image">
              <img
                src={images.testimonials.children}
                alt="Children"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = 'https://via.placeholder.com/500x400/2c2c2c/f4c430?text=Children';
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Counter Section */}
      <section className="counter-section">
        <div className="container">
          <div className="counter-content">
            <div className="counter-number">27,514</div>
            <p className="counter-text">
              {t('home.counter.text')} <span className="heart-emoji">❤️</span> {t('home.counter.voluntaries')}
            </p>
          </div>
        </div>
      </section>

      {/* Impact Modal */}
      {showImpactModal && (
        <ImpactModal onClose={() => setShowImpactModal(false)} />
      )}
    </div>
  );
};

const CauseCard: React.FC<{
  image: string;
  progress: number;
  donation: string;
  goal: string;
  title: string;
  description: string;
  buttonText: string;
}> = ({ image, progress, donation, goal, title, description, buttonText }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleDonateClick = () => {
    navigate('/donate-anonymously');
  };

  return (
    <div className="cause-card">
      <img
        src={image}
        alt={title}
        onError={(e) => {
          const target = e.target as HTMLImageElement;
          target.src = 'https://via.placeholder.com/400x250/f4c430/333333?text=Cause';
        }}
      />
      <div className="cause-progress">
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${progress}%` }}></div>
          <div className="progress-badge" style={{ left: `${progress}%` }}>
            {progress}%
          </div>
        </div>
        <p className="donation-info">
          {t('home.recentCauses.childEducation.donation')} {donation}/${goal}
        </p>
      </div>
      <div className="cause-details">
        <h3>{title}</h3>
        <p>{description}</p>
        <button className="donate-button" onClick={handleDonateClick}>
          {buttonText}
        </button>
      </div>
    </div>
  );
};

const StatisticsSection: React.FC<{
  statistics: Array<{ icon: string; number: string; label: string }>;
}> = ({ statistics }) => {
  const [scrollTrigger, setScrollTrigger] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);
  const [hasScrolledIntoView, setHasScrolledIntoView] = useState(false);

  // Handle scroll-triggered animation for all cards
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasScrolledIntoView) {
            setHasScrolledIntoView(true);
            setScrollTrigger((prev) => prev + 1);
          }
        });
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [hasScrolledIntoView]);

  return (
    <section
      ref={sectionRef}
      className="statistics-section"
      style={{ backgroundImage: `url(${images.statistics.background})` }}
    >
      <div className="statistics-overlay"></div>
      <div className="container">
        <div className="statistics-grid">
          {statistics.map((stat, index) => (
            <StatisticCard
              key={index}
              icon={stat.icon}
              number={stat.number}
              label={stat.label}
              scrollTrigger={scrollTrigger}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const StatisticCard: React.FC<{
  icon: string;
  number: string;
  label: string;
  scrollTrigger: number;
}> = ({ icon, number, label, scrollTrigger }) => {
  const [count, setCount] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const animationTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  const animateNumber = useCallback(() => {
    // Clear any existing animation
    if (animationTimerRef.current) {
      clearInterval(animationTimerRef.current);
    }

    // Reset count
    setCount(0);
    setIsAnimating(true);

    const targetNumber = parseInt(number.replace(/,/g, ''), 10);
    const duration = 2000; // 2 seconds
    const steps = 60;
    const increment = targetNumber / steps;
    const stepDuration = duration / steps;
    let current = 0;

    animationTimerRef.current = setInterval(() => {
      current += increment;
      if (current >= targetNumber) {
        setCount(targetNumber);
        setIsAnimating(false);
        if (animationTimerRef.current) {
          clearInterval(animationTimerRef.current);
        }
      } else {
        setCount(Math.floor(current));
      }
    }, stepDuration);
  }, [number]);

  // Trigger animation when scrollTrigger changes (scroll into view)
  useEffect(() => {
    if (scrollTrigger > 0) {
      animateNumber();
    }
  }, [scrollTrigger, animateNumber]);

  // Handle scroll visibility for entrance animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.3 }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current);
      }
      if (animationTimerRef.current) {
        clearInterval(animationTimerRef.current);
      }
    };
  }, []);

  const handleClick = () => {
    // Only this specific card should animate on click
    animateNumber();
  };

  const formatNumber = (num: number) => {
    return num.toLocaleString();
  };

  return (
    <div
      ref={cardRef}
      className={`statistic-card ${isVisible ? 'animate-in' : ''} ${isAnimating ? 'animating' : ''}`}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <div className="statistic-icon">{icon}</div>
      <div className="statistic-number">{formatNumber(count)}</div>
      <div className="statistic-label">{label}</div>
    </div>
  );
};

const DonaterCard: React.FC<{
  image: string;
  name: string;
  role: string;
  amount: string;
}> = ({ image, name, role, amount }) => {
  const { t } = useTranslation();
  return (
    <div className="donater-card">
      <img
        src={image}
        alt={name}
        className="donater-image"
        onError={(e) => {
          const target = e.target as HTMLImageElement;
          target.src = 'https://via.placeholder.com/120x120/f4c430/333333?text=' + name.charAt(0);
        }}
      />
      <h4>{name}</h4>
      <p className="donater-role">{role}</p>
      <p className="donater-amount">
        {t('home.latestDonaters.donated')} <span className="amount-highlight">{amount}</span>
      </p>
    </div>
  );
};

const BlogCard: React.FC<{ image: string }> = ({ image }) => {
  const { t } = useTranslation();
  return (
    <div className="blog-card">
      <img
        src={image}
        alt="Blog Post"
        onError={(e) => {
          const target = e.target as HTMLImageElement;
          target.src = 'https://via.placeholder.com/400x300/f4c430/333333?text=Blog';
        }}
      />
      <div className="blog-overlay">
        <button className="read-more-button">{t('home.blog.readMore')}</button>
      </div>
    </div>
  );
};

export default Home;

